/**
 * 文件弹窗预览工具（移动端优化）
 *
 * 策略：对所有非图片文件统一尝试 iframe 预览，
 * - 浏览器能内联渲染的文件（PDF/TXT/HTML 等）直接显示
 * - 不能渲染的文件（Word/Excel 等）5 秒后显示兜底卡片
 * - 底部始终保留「下载文件」和「在浏览器中打开」按钮
 *
 * 使用：import { previewFileInPopup } from './filePreviewPopup';
 *       previewFileInPopup(url, fileName);
 */

let activePopup = null;
let fallbackTimer = null;

export function previewFileInPopup(url, fileName = '') {
  if (activePopup) {
    closeFilePreviewPopup();
  }

  // 注入样式（只注入一次）
  if (!document.getElementById('fp-style')) {
    const style = document.createElement('style');
    style.id = 'fp-style';
    style.textContent = `
      @keyframes fp-fade-in { from { opacity: 0; } to { opacity: 1; } }
      @keyframes fp-spin { to { transform: rotate(360deg); } }
    `;
    document.head.appendChild(style);
  }

  // 创建全屏遮罩
  const overlay = document.createElement('div');
  overlay.className = 'file-preview-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.85);
    z-index: 3000;
    display: flex;
    flex-direction: column;
    animation: fp-fade-in 0.2s ease;
  `;

  // 顶部标题栏
  const header = document.createElement('div');
  header.style.cssText = `
    flex-shrink: 0;
    padding: 12px 16px;
    padding-top: max(12px, env(safe-area-inset-top));
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #ebedf0;
  `;

  const title = document.createElement('span');
  title.textContent = fileName || '文件预览';
  title.style.cssText = `
    font-size: 15px;
    font-weight: 500;
    color: #323233;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 40px);
  `;

  const closeBtn = document.createElement('div');
  closeBtn.innerHTML = `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M6 6L18 18M6 18L18 6" stroke="#969799" stroke-width="2" stroke-linecap="round"/>
    </svg>
  `;
  closeBtn.style.cssText = `
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
  `;
  closeBtn.addEventListener('click', closeFilePreviewPopup);

  header.appendChild(title);
  header.appendChild(closeBtn);

  // 内容区域
  const body = document.createElement('div');
  body.style.cssText = `
    flex: 1;
    overflow: hidden;
    position: relative;
    background: #f7f8fa;
  `;

  // 加载动画
  const loading = document.createElement('div');
  loading.style.cssText = `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: #969799;
    font-size: 14px;
    z-index: 1;
  `;
  loading.innerHTML = `
    <div style="width: 32px; height: 32px; border: 3px solid #dcdee0; border-top-color: #1989fa; border-radius: 50%; animation: fp-spin 0.8s linear infinite;"></div>
    <span>正在加载文件…</span>
  `;

  // iframe 尝试预览
  const iframe = document.createElement('iframe');
  iframe.style.cssText = `
    width: 100%;
    height: 100%;
    border: none;
    display: block;
    position: relative;
    z-index: 2;
    background: #fff;
  `;
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('allowfullscreen', '');
  iframe.src = url;

  iframe.addEventListener('load', () => {
    loading.style.display = 'none';
    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  });

  // 5 秒超时兜底
  fallbackTimer = setTimeout(() => {
    if (loading.style.display !== 'none') {
      // iframe 未触发 load，显示兜底卡片
      loading.style.display = 'none';
      iframe.style.display = 'none';
      fallbackCard.style.display = 'flex';
    }
  }, 5000);

  // 兜底卡片（默认隐藏）
  const fallbackCard = document.createElement('div');
  fallbackCard.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #f7f8fa;
    display: none;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px;
    text-align: center;
    z-index: 3;
  `;

  const fileIcon = document.createElement('div');
  fileIcon.innerHTML = `
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
      <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#1989fa" opacity="0.15"/>
      <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="#1989fa" stroke-width="1.5" stroke-linejoin="round"/>
      <path d="M14 2V8H20" stroke="#1989fa" stroke-width="1.5" stroke-linejoin="round"/>
    </svg>
  `;
  fileIcon.style.marginBottom = '16px';

  const nameEl = document.createElement('div');
  nameEl.textContent = fileName || '文件';
  nameEl.style.cssText = `
    font-size: 15px;
    color: #323233;
    word-break: break-all;
    margin-bottom: 8px;
    max-width: 280px;
  `;

  const tip = document.createElement('div');
  tip.textContent = '该类型文件无法在此页面内预览，请使用下方按钮下载查看';
  tip.style.cssText = `
    font-size: 13px;
    color: #969799;
    margin-bottom: 24px;
    max-width: 260px;
    line-height: 1.5;
  `;

  fallbackCard.appendChild(fileIcon);
  fallbackCard.appendChild(nameEl);
  fallbackCard.appendChild(tip);

  body.appendChild(loading);
  body.appendChild(iframe);
  body.appendChild(fallbackCard);

  // 底部操作栏：始终显示
  const footer = document.createElement('div');
  footer.style.cssText = `
    flex-shrink: 0;
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    background: #fff;
    border-top: 1px solid #ebedf0;
  `;

  const downloadBtn = document.createElement('a');
  downloadBtn.href = url;
  downloadBtn.download = fileName || '';
  downloadBtn.textContent = '下载文件';
  downloadBtn.style.cssText = `
    flex: 1;
    display: block;
    text-align: center;
    padding: 10px 0;
    background: #1989fa;
    color: #fff;
    font-size: 15px;
    border-radius: 22px;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
  `;

  const openBtn = document.createElement('a');
  openBtn.href = url;
  openBtn.target = '_blank';
  openBtn.rel = 'noopener';
  openBtn.textContent = '在浏览器中打开';
  openBtn.style.cssText = `
    flex: 1;
    display: block;
    text-align: center;
    padding: 10px 0;
    background: #f7f8fa;
    color: #1989fa;
    font-size: 15px;
    border-radius: 22px;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
  `;

  footer.appendChild(downloadBtn);
  footer.appendChild(openBtn);

  // 组装
  overlay.appendChild(header);
  overlay.appendChild(body);
  overlay.appendChild(footer);
  document.body.appendChild(overlay);
  activePopup = overlay;

  // 阻止 body 滚动
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  activePopup._cleanup = () => {
    document.body.style.overflow = prevOverflow;
    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };
}

export function closeFilePreviewPopup() {
  if (activePopup) {
    if (activePopup._cleanup) {
      activePopup._cleanup();
    }
    if (activePopup.parentNode) {
      activePopup.parentNode.removeChild(activePopup);
    }
    activePopup = null;
  }
}
